import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  useNotificationsEmitter,
  useRegisterNotificationCategory,
} from "$global-providers/notifications-provider";
import { useChangedOrgPrices } from "$global-providers/rxdb-provider/price-review/use-changed-org-prices";
import { useEffect, useRef } from "react";

/**
 * Category A adapter for ADR-004 §8: emits `prices.org-push` when org prices
 * on the facility's linked org price-list have moved past the user's
 * acknowledgement, and recalls when the user catches up.
 *
 * The change scan is shared with the review banner (`useChangedOrgPrices`) so
 * the bell count and the on-page banner count can never disagree.
 */
export const useNotificationsAdapterPrices = () => {
  const { emit, recall } = useNotificationsEmitter();
  const [adapterEnabled, { loading: adapterFlagLoading }] =
    useFeatureFlag<boolean>(featureFlags.notifications.center);
  const [priceListRxdbEnabled, { loading: priceListRxdbFlagLoading }] =
    useFeatureFlag<boolean>(featureFlags.billing.priceListRxdb);
  useRegisterNotificationCategory(PRICE_PUSH_CATEGORY);

  const {
    isFacilityAdmin,
    facilityPriceListId,
    linkedOrgPriceListId,
    acknowledgedAt: ackAt,
    scan: changed,
  } = useChangedOrgPrices();

  const flagsLoading = adapterFlagLoading || priceListRxdbFlagLoading;
  const scanInFlight =
    changed.loading ||
    changed.scannedPriceListId !== linkedOrgPriceListId ||
    changed.scannedAcknowledgedAt !== ackAt;
  // `ready` is split from `gated`: ready means we have a positive signal we
  // can act on. `gated` means we're definitively *not* eligible — flags off,
  // wrong role, no price-list. The recall path only fires for `gated`, not
  // for the in-flight scan loading state, so we don't remove a notification
  // and immediately re-emit it when inputs change.
  const gated =
    !flagsLoading &&
    (!adapterEnabled ||
      !priceListRxdbEnabled ||
      !isFacilityAdmin ||
      !facilityPriceListId);
  const ready =
    !flagsLoading &&
    adapterEnabled &&
    priceListRxdbEnabled &&
    isFacilityAdmin &&
    !!facilityPriceListId &&
    !scanInFlight;
  const count = changed.count;
  const version = changed.maxUpdatedAt
    ? `${changed.count}-${changed.maxUpdatedAt}`
    : null;

  // Track the facility's price-list id so when it changes (admin re-points the
  // facility) we recall the stale-key row instead of leaving an orphan in the
  // bell that points at the previous list.
  const previousPriceListIdRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = previousPriceListIdRef.current;
    if (prev && prev !== facilityPriceListId) {
      void recall(`${PRICE_PUSH_CATEGORY.categoryKey}:${prev}`);
    }
    previousPriceListIdRef.current = facilityPriceListId;
  }, [facilityPriceListId, recall]);

  useEffect(() => {
    if (!facilityPriceListId) return;
    const key = `${PRICE_PUSH_CATEGORY.categoryKey}:${facilityPriceListId}`;
    if (gated) {
      void recall(key);
      return;
    }
    if (!ready) return;
    if (count > 0 && version) {
      void emit({
        key,
        title: "Organisation prices updated",
        body: `${count} ${
          count === 1 ? "price" : "prices"
        } changed since you last reviewed`,
        severity: "INFO",
        category: PRICE_PUSH_CATEGORY.categoryKey,
        icon: "💰",
        actionUrl: `/price-list/${facilityPriceListId}?review=true`,
        version,
      });
    } else {
      void recall(key);
    }
  }, [gated, ready, facilityPriceListId, count, version, emit, recall]);
};

export const PRICE_PUSH_CATEGORY = {
  categoryKey: "prices.org-push",
  label: "Price list changes",
  description: "When the organisation pushes updates",
} as const;
